import React from 'react';
import { PublicLayout } from '@/components/layout/PublicLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Helmet } from 'react-helmet-async';
import { Shield, Lock, Eye, Database, Users, FileText, Mail } from 'lucide-react';

export default function PrivacyPage() {
  const effectiveDate = "January 11, 2026";

  return (
    <PublicLayout>
      <Helmet>
        <title>Privacy Policy | Kosha Tax Consultants</title>
        <meta name="description" content="Learn how Kosha Tax Consultants collects, uses, and protects your personal information. Our commitment to your privacy and data security." />
      </Helmet>

      <div className="bg-gradient-to-b from-blue-50 to-white dark:from-gray-900 dark:to-gray-800 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-4xl font-bold text-center mb-4 text-foreground">Privacy Policy</h1>
            <p className="text-center text-muted-foreground mb-8">Effective Date: {effectiveDate}</p>
            
            <Card className="shadow-lg">
              <CardContent className="prose prose-lg dark:prose-invert max-w-none p-6 md:p-8">
                {/* Introduction */}
                <section className="mb-8">
                  <div className="flex items-center gap-2 mb-4">
                    <Shield className="h-5 w-5 text-primary flex-shrink-0" />
                    <h2 className="text-2xl font-semibold m-0">Introduction</h2>
                  </div>
                  <p>
                    This Privacy Policy applies to <strong>Kosha Tax Consultants</strong>, a DBA (doing business as) of 
                    <strong> Annapurna Events LLC</strong>, a Texas limited liability company (collectively, &quot;we,&quot; &quot;us,&quot; or &quot;our&quot;).
                  </p>
                  <p>
                    We are committed to protecting your privacy. This policy explains how we collect, use, disclose, and 
                    safeguard your information when you use our tax preparation and consulting services.
                  </p>
                </section>

                {/* Information We Collect */}
                <section className="mb-8">
                  <div className="flex items-center gap-2 mb-4">
                    <Database className="h-5 w-5 text-primary flex-shrink-0" />
                    <h2 className="text-2xl font-semibold m-0">Information We Collect</h2>
                  </div>
                  <p>We may collect the following categories of information:</p>
                  <ul>
                    <li><strong>Account Information:</strong> Name, email address, phone number, and login credentials.</li>
                    <li><strong>Contact Information:</strong> Mailing address and communication preferences.</li>
                    <li><strong>Tax Documents:</strong> W-2s, 1099s, and other tax-related documents you upload.</li>
                    <li><strong>Personal Identifiers:</strong> Social Security Numbers (SSN), Individual Taxpayer Identification Numbers (ITIN), and dates of birth.</li>
                    <li><strong>Payment Information:</strong> Billing details processed securely by Stripe. We do not store your full card details.</li>
                    <li><strong>Device and Log Data:</strong> IP address, browser type, operating system, and usage patterns.</li>
                    <li>
                      <strong>Cookies:</strong> Small data files to improve your experience. For more information, 
                      see our <a href="/cookies" className="text-primary hover:underline">Cookie Policy</a>.
                    </li>
                  </ul>
                </section>

                {/* How We Use Your Information */}
                <section className="mb-8">
                  <div className="flex items-center gap-2 mb-4">
                    <Eye className="h-5 w-5 text-primary flex-shrink-0" />
                    <h2 className="text-2xl font-semibold m-0">How We Use Your Information</h2>
                  </div>
                  <p>We use your information for the following purposes:</p>
                  <ul>
                    <li><strong>Provide Services:</strong> To prepare, review, and support the filing of your tax returns.</li>
                    <li><strong>Communicate:</strong> To send you updates, confirmations, and respond to inquiries.</li>
                    <li><strong>Legal Compliance:</strong> To comply with applicable laws, regulations, and legal processes.</li>
                    <li><strong>Security:</strong> To protect our platform, detect fraud, and prevent unauthorized access.</li>
                    <li><strong>Fraud Prevention:</strong> To identify and prevent fraudulent or illegal activities.</li>
                    <li><strong>Improve Services:</strong> To analyze usage and enhance our platform and offerings.</li>
                  </ul>
                </section>

                {/* Information Sharing */}
                <section className="mb-8">
                  <div className="flex items-center gap-2 mb-4">
                    <Users className="h-5 w-5 text-primary flex-shrink-0" />
                    <h2 className="text-2xl font-semibold m-0">Information Sharing & Data Protection</h2>
                  </div>
                  
                  {/* Strong No-Sell Statement */}
                  <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 mb-4 not-prose">
                    <p className="font-bold text-green-800 dark:text-green-200 mb-2">
                      We Never Sell, Rent, or Trade Your Personal Data
                    </p>
                    <p className="text-green-700 dark:text-green-300 text-sm">
                      Your personal information is never sold, rented, traded, or otherwise shared with third parties 
                      for their marketing or commercial purposes. Your data belongs to you.
                    </p>
                  </div>

                  <p className="font-medium mb-2">Limited Data Sharing for Service Operation Only:</p>
                  <p>
                    We share your information <strong>only</strong> with trusted service providers who help us operate 
                    our business, and only to the extent necessary to provide our services to you:
                  </p>
                  <ul>
                    <li>
                      <strong>Payment Processing (Stripe):</strong> To securely process your payments. 
                      We do not store your full credit card details.
                    </li>
                    <li>
                      <strong>Cloud Hosting Providers:</strong> To store and protect your data securely.
                    </li>
                    <li>
                      <strong>Communication Providers:</strong> To send you important updates about your filings via email or SMS.
                    </li>
                  </ul>

                  {/* Contractual Restrictions */}
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mt-4 not-prose">
                    <p className="font-medium text-blue-800 dark:text-blue-200 mb-2">
                      Service Provider Restrictions
                    </p>
                    <ul className="text-blue-700 dark:text-blue-300 text-sm space-y-2">
                      <li>• All service providers are contractually bound to protect your data</li>
                      <li>• They are prohibited from using your data for their own purposes</li>
                      <li>• Third-party companies do not have independent access to view or use your personal data</li>
                      <li>• Data is shared only to the minimum extent necessary to operate our services</li>
                    </ul>
                  </div>

                  <p className="mt-4">We may also share information in these limited circumstances:</p>
                  <ul>
                    <li><strong>Legal Requirements:</strong> When required by law, court order, or government request.</li>
                    <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets (with equivalent privacy protections).</li>
                    <li><strong>With Your Consent:</strong> When you explicitly authorize us to share your information.</li>
                  </ul>
                </section>

                {/* Data Security */}
                <section className="mb-8">
                  <div className="flex items-center gap-2 mb-4">
                    <Lock className="h-5 w-5 text-primary flex-shrink-0" />
                    <h2 className="text-2xl font-semibold m-0">Data Security</h2>
                  </div>
                  <p>We implement robust security measures to protect your information:</p>
                  <ul>
                    <li><strong>Encryption at Rest:</strong> Social Security Numbers (SSNs) and other sensitive identifiers are encrypted using AES-256 encryption.</li>
                    <li><strong>Encryption in Transit:</strong> All data transmitted between your device and our servers is protected using HTTPS/TLS encryption.</li>
                    <li><strong>Access Controls:</strong> Only authorized staff members can access your documents and information for the purpose of providing services.</li>
                    <li><strong>Audit Logging:</strong> We maintain logs of access to sensitive data for security and compliance purposes.</li>
                  </ul>
                  <p className="text-sm italic">
                    Please note: While we strive to protect your information, no method of transmission or storage is 100% secure. 
                    We cannot guarantee absolute security.
                  </p>
                </section>

                {/* Data Retention */}
                <section className="mb-8">
                  <div className="flex items-center gap-2 mb-4">
                    <FileText className="h-5 w-5 text-primary flex-shrink-0" />
                    <h2 className="text-2xl font-semibold m-0">Data Retention</h2>
                  </div>
                  <p>
                    We retain your information only as long as necessary to provide our services and comply with legal and 
                    tax compliance obligations. Tax records may be retained for a minimum of seven (7) years as required by 
                    IRS regulations, or longer if required by applicable law.
                  </p>
                </section>

                {/* Your Rights */}
                <section className="mb-8">
                  <h2 className="text-2xl font-semibold">Your Rights</h2>
                  <p>Depending on your location, you may have the following rights:</p>
                  <ul>
                    <li><strong>Access:</strong> Request a copy of the personal information we hold about you.</li>
                    <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information.</li>
                    <li><strong>Deletion:</strong> Request deletion of your personal information, subject to legal retention requirements.</li>
                    <li><strong>Opt-Out:</strong> Opt out of marketing communications at any time.</li>
                  </ul>
                  <p>To exercise these rights, please contact us using the information provided below.</p>
                </section>

                {/* Children's Privacy */}
                <section className="mb-8">
                  <h2 className="text-2xl font-semibold">Children&apos;s Privacy</h2>
                  <p>
                    Our services are not intended for individuals under the age of 18. We do not knowingly collect 
                    personal information from children. If you believe we have collected information from a child, 
                    please contact us immediately.
                  </p>
                </section>

                {/* Contact Information */}
                <section className="mb-8">
                  <div className="flex items-center gap-2 mb-4">
                    <Mail className="h-5 w-5 text-primary flex-shrink-0" />
                    <h2 className="text-2xl font-semibold m-0">Contact Us</h2>
                  </div>
                  <p>For privacy-related inquiries or to exercise your rights, please contact us:</p>
                  <div className="bg-muted/50 p-4 rounded-lg not-prose">
                    <p className="font-medium">Kosha Tax Consultants</p>
                    <p className="text-muted-foreground">A DBA of Annapurna Events LLC (Texas)</p>
                    <p className="text-muted-foreground">Email: help@koshatax.com</p>
                  </div>
                </section>

                {/* Changes to This Policy */}
                <section>
                  <h2 className="text-2xl font-semibold">Changes to This Policy</h2>
                  <p>
                    We may update this Privacy Policy from time to time. When we make changes, we will update the 
                    &quot;Effective Date&quot; at the top of this page. We encourage you to review this policy periodically. 
                    Your continued use of our services after changes are posted constitutes acceptance of the updated policy.
                  </p>
                </section>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}
